import { FiUser } from 'react-icons/fi';
import { AccountPanel } from '@/shared/layout/profile-panel';
import type {
  AccountPanelGroup,
  AccountPanelMenuItem,
  ProfileIdentity,
} from '@/shared/layout/profile-panel';
import { topBarIconButtonClasses, topBarIconClasses } from '@/shared/layout/top-bar/styles';
import {
  useProfilePanelOpen,
  useSetProfilePanelOpen,
} from '@/layouts/app-shell/components/ui-store';

/**
 * The top bar's profile control, and the panel it opens.
 *
 * The trigger and the panel live in one component because they are one
 * interaction: the button carries `aria-expanded` for the panel, and the panel
 * must give focus back to this exact button when it closes. Separating them
 * would mean lifting that pairing into the shell as two pieces of state that
 * have to agree.
 *
 * They are siblings rather than nested, because the panel portals to
 * `document.body` — a trigger wrapped around it would put the button outside
 * the dialog it claims to control.
 */
export function ProfileButton({
  identity,
  jumpTo,
  groups,
  onSignOut,
}: {
  identity: ProfileIdentity;
  jumpTo: ReadonlyArray<AccountPanelMenuItem>;
  groups: ReadonlyArray<AccountPanelGroup>;
  onSignOut?: () => void;
}) {
  const open = useProfilePanelOpen();
  const setOpen = useSetProfilePanelOpen();

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={topBarIconButtonClasses}
        aria-label={`Account: ${identity.name}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Account"
      >
        <FiUser className={topBarIconClasses} aria-hidden="true" />
      </button>
      <AccountPanel
        open={open}
        onOpenChange={setOpen}
        identity={identity}
        jumpTo={jumpTo}
        groups={groups}
        onSignOut={onSignOut}
      />
    </>
  );
}
